// Phase 25 iter-8 — Conversation detail rendered as Outlet of MessagesPage.
//
// Polleamos a conversa cada 8 s. Cando o user envía unha mensaxe, optimistic
// append + reconciliación na próxima poll.

import { Send } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { ConversationDetail } from '@leyrochain/shared';

import { getConversation, markConversationRead, sendMessage } from '../api/conversations';
import { describeAxiosError } from '../api/errors';
import Avatar from '../components/ui/Avatar';
import Spinner from '../components/ui/Spinner';
import { useAuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

const POLL_INTERVAL_MS = 8_000;

export default function ConversationView(): JSX.Element {
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const { user } = useAuthContext();
  const { addToast } = useToast();
  const [data, setData] = useState<ConversationDetail | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await getConversation(id);
      setData(res);
      setError(null);
      // Marcar como lido — fire-and-forget.
      void markConversationRead(id).catch(() => undefined);
    } catch (err) {
      setError(describeAxiosError(err, 'Non se puido cargar a conversa.'));
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    void refresh();
    const t = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => window.clearInterval(t);
  }, [id, refresh]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [data?.messages.length]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setSending(true);
    try {
      await sendMessage(id, text);
      setDraft('');
      await refresh();
    } catch (err) {
      addToast(describeAxiosError(err, 'Non se puido enviar.'), 'error');
    } finally {
      setSending(false);
    }
  }

  if (!data) {
    return (
      <div className="border border-border rounded-2xl bg-white h-full min-h-[480px] flex items-center justify-center">
        {error ? (
          <p className="text-sm text-danger px-6 text-center">{error}</p>
        ) : (
          <Spinner size={22} />
        )}
      </div>
    );
  }

  const counter = data.buyer.id === user?.id ? data.seller : data.buyer;

  return (
    <div className="border border-border rounded-2xl bg-white flex flex-col h-full min-h-[480px] overflow-hidden">
      {/* Header */}
      <header className="flex items-center gap-3 p-4 border-b border-border">
        <Avatar user={counter} size={40} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-dark truncate">{counter.name}</p>
          <Link
            to={`/marketplace/animal/${data.sale.id}`}
            className="text-xs text-muted hover:text-rauxa truncate block"
          >
            Sobre {data.sale.animal_name ?? data.sale.animal_crotal} ·{' '}
            {(data.sale.price_cents / 100).toFixed(0)} €
          </Link>
        </div>
      </header>

      {/* Messages */}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-subtle/40">
        {data.messages.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-sm text-muted text-center px-6">
              Inicia a conversa cunha pregunta sobre o animal.
            </p>
          </div>
        ) : (
          data.messages.map((m) => {
            const mine = m.sender_id === user?.id;
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                    mine
                      ? 'bg-rauxa text-white rounded-br-sm'
                      : 'bg-white border border-border text-dark rounded-bl-sm'
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">{m.text}</p>
                  <p
                    className={`text-[10px] mt-1 ${mine ? 'text-white/70' : 'text-muted'}`}
                    title={new Date(m.created_at).toLocaleString('gl-ES')}
                  >
                    {new Date(m.created_at).toLocaleTimeString('gl-ES', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    {mine && m.read_at ? ' · Lida' : ''}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-2 p-3 border-t border-border bg-white"
      >
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Escribe unha mensaxe…"
          maxLength={2000}
          disabled={sending}
          className="flex-1 outline-none text-sm text-dark placeholder-muted px-3 py-2 bg-subtle rounded-xl"
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          className="btn-primary !px-4 !py-2 disabled:opacity-60"
          aria-label="Enviar"
        >
          {sending ? <Spinner size={14} /> : <Send size={16} />}
        </button>
      </form>
    </div>
  );
}
